import { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'

import { Collapsible } from 'app/KinerjaShared/components/Collapsible'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaShared/components/Card'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import {
  API_PATH_TUGAS_POKOK,
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIGetTugasPokok,
  useAPIPelaksanaanProgress,
  useAPISubmitTugasPokok,
} from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { isSelectableTugasPokok } from 'app/KinerjaGuruPage/utils/perencanaan'
import { resolvePerencanaanDetailData } from 'app/KinerjaShared/utils/perencanaan'
import {
  useAPIKinerja,
  useAPIContentKinerjaUtama,
} from 'api/kinerja/useAPIKinerja'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { PageGuardPamongPelaksanaanStatus } from 'app/KinerjaPamongPage/components/pelaksanaan/PageGuardPamongPelaksanaanStatus'

import type {
  MultiPeriodParams,
  KinerjaUtamaDetail,
  MultiPeriodPayload,
} from 'types/kinerja'
import type { PerencanaanGuruDetailData } from 'app/KinerjaGuruPage/types/perencanaan'

function Rows({ periodParams }: { periodParams?: MultiPeriodParams }) {
  // -----------------------------------------
  // Get content data for mapping
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPNF',
      periodParams,
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  // Content as map for constant time lookup
  const tugasPokokcontentsMap: Record<number, KinerjaUtamaDetail> =
    useMemo(() => {
      const contents = dataContentKinerjaUtama?.data?.kinerjaUtama || []

      return contents.reduce((acc, content) => {
        acc[content.id] = content
        return acc
      }, {} as Record<number, KinerjaUtamaDetail>)
    }, [dataContentKinerjaUtama?.data?.kinerjaUtama])

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe: 'SPNF',
        ...periodParams,
      }
    )

  const resolvedPerencanaanData: PerencanaanGuruDetailData = useMemo(
    () => resolvePerencanaanDetailData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  // -----------------------------------------
  // Resolve the mandatory and selectable tugas pokok
  // -----------------------------------------
  const {
    mandatoryTugasPokok,
    selectableTugasPokok,
  }: {
    mandatoryTugasPokok: Array<number>
    selectableTugasPokok: Array<number>
  } = useMemo(() => {
    // Use `reviewTugasPokokIds` instead of `tugasPokokIds` as it represents the final tugas pokok ids
    const tugasPokokIds = resolvedPerencanaanData.reviewTugasPokokIds

    const mandatory = []
    const selectable = []

    tugasPokokIds.forEach((id) => {
      const content = tugasPokokcontentsMap[id]

      if (content) {
        if (isSelectableTugasPokok(content.tag)) {
          selectable.push(content.id)
        } else {
          mandatory.push(content.id)
        }
      }
    })
    return {
      mandatoryTugasPokok: mandatory,
      selectableTugasPokok: selectable,
    }
  }, [resolvedPerencanaanData.reviewTugasPokokIds, tugasPokokcontentsMap])

  const isLoading = isPerencanaanLoading || isLoadingContentKinerjaUtama

  if (isLoading) {
    return <TableShimmer columns={2} rows={5} />
  }

  return (
    <>
      {/* Mandatory Tugas Pokok */}
      {mandatoryTugasPokok.map((tugasPokokId, index) => (
        <TableRow key={index}>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {index + 1}
          </Text>
          <Text as={TableRowItem}>
            {tugasPokokcontentsMap[tugasPokokId]?.rencanaHasilKerja}
          </Text>
        </TableRow>
      ))}

      {/* Selectable Tugas Pokok */}
      {selectableTugasPokok.length > 0 && (
        <TableRow>
          <Text as={TableRowItem} variant="body-sm" className="text-center">
            {mandatoryTugasPokok.length + 1}
          </Text>
          <Text as={TableRowItem}>
            <Collapsible title="Terlaksananya pembimbingan dan pelatihan dalam mewujudkan pembelajaran yang bermutu untuk semua">
              <ul className="pl-6 mt-2 space-y-3 list-disc">
                {selectableTugasPokok.map((item, index) => (
                  <Text as="li" key={index}>
                    {tugasPokokcontentsMap[item]?.targetKualitas}
                  </Text>
                ))}
              </ul>
            </Collapsible>
          </Text>
        </TableRow>
      )}
    </>
  )
}

type TugasPokokProps = {
  periodParams?: MultiPeriodPayload
}

const TugasPokok = (props: TugasPokokProps) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { periodYear, periodNo } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (props.periodParams) return props.periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [props.periodParams, periodNo, periodYear])

  // ----------------------------
  // Handle checkbox
  // ----------------------------
  const [isChecked, setIsChecked] = useState(false)

  const { data: dataTugasPokokState, isLoading: isLoadingTugasPokokState } =
    useAPIGetTugasPokok({
      params: {
        periodParams: {
          skp_year: resolvedPeriodParams.skpYear,
          skp_period: resolvedPeriodParams.skpPeriod,
        },
        tipe: 'SPNF',
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: dataPelaksanaan } = useAPIPelaksanaanProgress({
    tipe: 'SPNF',
    periodParams: {
      skp_year: resolvedPeriodParams.skpYear,
      skp_period: resolvedPeriodParams.skpPeriod,
    },
    opts: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // ----------------------------
  // Handle submission
  // ----------------------------
  const tugasPokokMutation = useAPISubmitTugasPokok()

  const handleSubmit = () => {
    if (isFormDisabled) return

    tugasPokokMutation.mutate(
      {
        tipe: 'SPNF',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_TUGAS_POKOK)
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
          toast({
            message: 'Berhasil simpan pernyataan',
            type: 'success',
          })
        },
        onError: () => {
          toast({
            message: 'Gagal simpan pernyataan. Coba lagi.',
          })
        },
      }
    )
  }

  // ----------------------------
  // Derived values
  // ----------------------------
  const state = dataTugasPokokState?.data?.state

  const isSubmitted = state === 'SUBMITTED' || state === 'APPROVED'
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isFormDisabled = isSubmitted || isEvaluated

  const isLoading = isLoadingTugasPokokState || tugasPokokMutation.isLoading

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH,
  })

  return (
    <main className="min-h-full px-8 pt-12 pb-8 bg-gray-10">
      <PageGuardPamongPelaksanaanStatus periodParams={resolvedPeriodParams}>
        <div className="mx-auto max-w-7xl">
          <Header
            title="Pelaksanaan Tugas Pokok"
            className="mb-9 print:hidden"
            onBack={handleBack}
          />

          <div className="flex items-start gap-8">
            <aside className="flex flex-grow-0 flex-shrink-0 gap-2 p-4 border rounded-lg w-96 border-gray-50">
              <Icon fontSize="sm" className="!text-xl !leading-5">
                info
              </Icon>

              <ul className="pl-6 space-y-2 list-disc">
                <Text as="li">
                  <strong>
                    Pamong Belajar tetap menyelesaikan Bukti Dukung Tugas Pokok
                    di luar sistem
                  </strong>{' '}
                  sesuai peraturan perundang-undangan.
                </Text>
                <Text as="li">
                  <strong>
                    Tidak perlu unggah Bukti Dukung di dalam sistem.
                  </strong>{' '}
                  Cukup tunjukkan Bukti Dukung ke Kepala SKB.
                </Text>
              </ul>
            </aside>
            <Card withBorder>
              <CardHeader>
                <Text variant="heading-lg" as="h2">
                  Tekan pernyataan di bawah jika Anda sudah melakukan
                  Pelaksanaan Tugas Pokok
                </Text>
              </CardHeader>

              <CardBody className="p-6 space-y-4">
                <Table withOuterBorder rounded>
                  <TableHeader>
                    <TableHeaderItem>No.</TableHeaderItem>
                    <TableHeaderItem>Rencana Hasil Kerja</TableHeaderItem>
                  </TableHeader>
                  <TableBody>
                    <Rows
                      periodParams={{
                        skp_year: resolvedPeriodParams.skpYear,
                        skp_period: resolvedPeriodParams.skpPeriod,
                      }}
                    />
                  </TableBody>
                </Table>

                <div className="flex p-4 rounded-lg bg-surface-selected-default">
                  <Checkbox
                    labelPosition="center"
                    className="flex"
                    disabled={isLoading || isFormDisabled}
                    checked={isChecked || isSubmitted}
                    onChange={(event) => {
                      setIsChecked(event.currentTarget.checked)
                    }}
                  >
                    <Text as="span" variant="body-sm-bold">
                      Saya menyatakan sudah melakukan Pelaksanaan Tugas Pokok
                    </Text>
                  </Checkbox>
                </div>
              </CardBody>

              {!isFormDisabled && (
                <CardFooter withSeparator={false} className="pt-0">
                  <Button
                    size="lg"
                    className="ml-auto"
                    isLoading={isLoading}
                    disabled={isLoading || isFormDisabled || !isChecked}
                    onClick={handleSubmit}
                  >
                    {isLoading ? 'Memuat..' : 'Simpan'}
                  </Button>
                </CardFooter>
              )}
            </Card>
          </div>
        </div>
      </PageGuardPamongPelaksanaanStatus>
    </main>
  )
}

export default TugasPokok
